/**
 * Ops GraphQL client for /graphql/ops mutations.
 *
 * The ops schema is separate from the public /graphql schema. The merged schema
 * covers it, so `graphql()` documents carry inferred result and variable types.
 * This client uses plain fetch (no urql) since ops mutations don't need
 * client-side caching or exchange pipelines.
 */

import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print, type DocumentNode } from 'graphql'
import { logError } from 'lib/logging'

type OpsGqlResult<T> = { data?: T; error?: string }

type OpsGqlInput = string | DocumentNode

const toQueryString = (input: OpsGqlInput): string => (typeof input === `string` ? input : print(input))

// Typed overload: when a `graphql()` document is passed, infer the result and
// variable types from the `TypedDocumentNode`. The string overload keeps the
// untyped `<T>` path for legacy inline usage.
export async function gqlOpsMutation<Data, Variables>(
  mutation: TypedDocumentNode<Data, Variables>,
  variables: Variables,
): Promise<OpsGqlResult<Data>>
export async function gqlOpsMutation<T = unknown>(
  mutation: OpsGqlInput,
  variables?: Record<string, unknown>,
): Promise<OpsGqlResult<T>>
export async function gqlOpsMutation<T = unknown>(
  mutation: OpsGqlInput,
  variables?: Record<string, unknown>,
): Promise<OpsGqlResult<T>> {
  try {
    const response = await window.fetch(`/graphql/ops`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ query: toQueryString(mutation), variables }),
    })
    const json = await response.json()
    if (json.errors?.length) {
      return { error: json.errors[0].message }
    }
    return { data: json.data as T }
  } catch (err) {
    logError(`[ops-graphql]`, `mutation failed`, err)
    return { error: (err as Error).message || `Unknown error` }
  }
}
